// client/src/pages/ProductosPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { getProductos, createProducto, updateProducto, deleteProducto } from "../api/productos.js";
import { getRestaurantes } from "../api/restaurantes.js";
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { getCategorias, createCategoria, updateCategoria} from "../api/categoriaProductos.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";

const ProductosPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [productos, setProductos] = useState([]);
    const [producto, setProducto] = useState({ nombre: "", precio: "", descripcion: "", imagen: null, estado: "activo", iva_codigo: "0", id_restaurante: "", id_categoria: "", });
    const [filteredProductos, setFilteredProductos] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [restaurantes, setRestaurantes] = useState([]);
    const inputRefs = useRef({});
    const { sortedItems: sortedProductos, sortConfig, setSortConfig } = useSortableData(filteredProductos);
    // Hook de paginación
    const { currentPage, setCurrentPage, paginatedItems: currentProductos, totalPages, itemsPerPage } = usePagination(sortedProductos, 20);
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    // ===== MODAL PARA CREAR NUEVA CATEGORÍA =====
    const [categorias, setCategorias] = useState([]);
    const [categoriaModal, setCategoriaModal] = useState(false);
    const [nuevaCategoria, setNuevaCategoria] = useState({ nombre: "", descripcion: "", estado: "activo", id_restaurante: "", });
    // Cargar restaurantes
    useEffect(() => {
        const loadRestaurantes = async () => {
        try {
            const data = await getRestaurantes();
            setRestaurantes(Array.isArray(data) ? data : data.restaurantes || []);
        } catch (err) {
            console.error("Error al cargar restaurantes:", err);
        }
        };
        loadRestaurantes();
    }, []);

    // Cargar productos
    const loadProductos = async () => {
    try {
        const data = await getProductos();
        const lista = Array.isArray(data) ? data : data.productos || [];
        setProductos(lista);
        setFilteredProductos(lista);
    } catch (err) {
        console.error("Error al cargar productos:", err);
    }
    };

    useEffect(() => {
        loadProductos();
    }, []);

    // Filtro
    useEffect(() => {
        const filtered = productos.filter((p) =>
        Object.values(p).some((val) =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
        );
        setFilteredProductos(filtered);
    }, [searchTerm, productos]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (name === "imagen") {
            const file = files[0];
            if (file) {
            setProducto({ ...producto, imagen: file });
            //Crear una URL temporal para previsualización
            const previewURL = URL.createObjectURL(file);
            setPreviewImage(previewURL);
            }
        } else {
            setProducto({ ...producto, [name]: value });
        }
    };


    const resetForm = () => {
        setProducto({ 
            nombre: "", 
            precio: "", 
            descripcion: "", 
            imagen: null, 
            estado: "activo", 
            iva_codigo: "0", 
            id_restaurante: usuarioLogueado?.isSuperAdmin ? "" : usuarioLogueado?.id_restaurante || "", 
            id_categoria: "", 
        });
        setPreviewImage(null);
        setErrors({});
        setEditMode(false);
    };

    const validateProducto = (data) => {
        const newErrors = {};
        if (!data.nombre.trim()) newErrors.nombre = "El nombre es obligatorio.";
        if (!data.precio || isNaN(data.precio)) newErrors.precio = "Ingrese un precio válido.";
        if (!data.descripcion.trim()) newErrors.descripcion = "La descripción es obligatoria.";
        // Solo validar id_restaurante si es SuperAdmin
        if (usuarioLogueado?.isSuperAdmin && !data.id_restaurante) {
            newErrors.id_restaurante = "Seleccione un restaurante.";
        }
        if (!data.id_categoria) newErrors.id_categoria = "Seleccione una categoría.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("🟢 handleSubmit ejecutado");
        const validationErrors = validateProducto(producto);
        if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        const firstErrorField = Object.keys(validationErrors)[0];
        const fieldRef = inputRefs.current[firstErrorField];
        if (fieldRef?.scrollIntoView) {
            fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
            fieldRef.focus();
        }
        return;
        }

        try {
        console.log(" Enviando producto:", producto);
        
        const productoData = { ...producto };
        
        // Si no es SuperAdmin, asignar automáticamente el id_restaurante
        if (!usuarioLogueado?.isSuperAdmin) {
            productoData.id_restaurante = usuarioLogueado?.id_restaurante;
        }
        
        if (editMode) {
            await updateProducto(producto.id_producto, productoData);
            alert("Producto actualizado correctamente");
        } else {
            await createProducto(productoData);
            alert("Producto creado correctamente");
        }

        resetForm();
        setModalOpen(false);
        loadProductos();
        } catch (err) {
        console.error(" Error del backend:", err.response?.data || err.message);
        const backendMsg =
            err.response?.data?.error ||
            err.response?.data?.message ||
            "Error al guardar producto.";
        setErrors({ general: backendMsg });
        alert(backendMsg);
        }
    };

    const handleEdit = (p) => {
        setProducto(p);
        // si el producto tiene imagen, mostrarla como previsualización
        if (p.imagen) {
            const imageUrl = p.imagen.startsWith("http")
            ? p.imagen
            : `${process.env.REACT_APP_API_URL}${p.imagen}`;
            setPreviewImage(imageUrl);
        } else {
            setPreviewImage(null);
        }
        setEditMode(true);
        setModalOpen(true);
    };


    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar este producto?")) {
        await deleteProducto(id);
        loadProductos();
        }
    };

    // Cargar categorías
    useEffect(() => {
    const loadCategorias = async () => {
        try {
        const data = await getCategorias();
        setCategorias(Array.isArray(data) ? data : data.categorias || []);
        } catch (err) {
        console.error("Error al cargar categorías:", err);
        }
    };

    loadCategorias();
    }, []);

    // Actualizar categorías cuando se crea una nueva
    const loadCategorias = async () => {
    try {
        const data = await getCategorias();
        setCategorias(Array.isArray(data) ? data : data.categorias || []);
    } catch (err) {
        console.error("Error al cargar categorías:", err);
    }
    };

    const handleChangeCategoria = (e) => {
    const { name, value } = e.target;
    setNuevaCategoria({ ...nuevaCategoria, [name]: value });
    };

    const handleCreateCategoria = async (e) => {
        e.preventDefault();
        if (!nuevaCategoria.nombre.trim()) {
            alert("El nombre de la categoría es obligatorio");
            return;
        }
        
        try {
            // Si no es SuperAdmin, asignar automáticamente el id_restaurante del usuario logueado
            const categoriaConRestaurante = {
                ...nuevaCategoria,
                id_restaurante: usuarioLogueado?.isSuperAdmin 
                    ? Number(nuevaCategoria.id_restaurante) 
                    : Number(usuarioLogueado?.id_restaurante)
            };
            
            // Validar que se tenga un id_restaurante
            if (!categoriaConRestaurante.id_restaurante) {
                alert("No se pudo determinar el restaurante. Por favor, contacte al administrador.");
                return;
            }
            
            await createCategoria(categoriaConRestaurante);
            alert("✅ Categoría creada correctamente");
            setCategoriaModal(false);
            setNuevaCategoria({ nombre: "", descripcion: "", estado: "activo", id_restaurante: "", });

            await loadCategorias();
        } catch (err) {
            console.error("Error al crear categoría:", err);
            alert(err.response?.data?.message || "Error al crear la categoría");
        }
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-center">Gestión de Productos</h1>

        {/* Buscador + botón */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <input type="text" placeholder="🔍 Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
            <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700" > ➕ Nuevo Producto </button>
        </div>

        {/* Tabla */}
        <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
            <table className="min-w-full text-sm text-left divide-y divide-gray-200">
            <thead className="bg-gray-100 text-gray-700 font-semibold">
                <tr>
                <SortableHeader label="N" sortKey="id_producto" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Nombre" sortKey="nombre" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                {usuarioLogueado?.isSuperAdmin && (
                    <SortableHeader label="Restaurante" sortKey="restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                )}
                <SortableHeader label="Categoria" sortKey="categoria" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Precio" sortKey="precio" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="IVA %" sortKey="iva_codigo" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <th className="px-4 py-2">Imagen</th>
                <th className="px-4 py-2 text-center">Acciones</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
            {currentProductos.length > 0 ? (
                currentProductos.map((p, idx) => (
                <tr key={p.id_producto} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                    <td className="px-4 py-2">{p.nombre}</td>
                    {usuarioLogueado?.isSuperAdmin && (
                        <td className="px-4 py-2">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                {p.restaurante?.nombre_comercial || "Sin restaurante"}
                            </span>
                        </td>
                    )}
                    <td className="px-4 py-2">{p.categoria ? p.categoria.nombre : "—"}</td>
                    <td className="px-4 py-2">${parseFloat(p.precio).toFixed(2)}</td>
                    <td className="px-4 py-2">{p.estado}</td>
                    <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                            p.iva_codigo === '0' ? 'bg-green-100 text-green-800' :
                            p.iva_codigo === '4' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                            {p.iva_codigo === '0' ? 'IVA 0%' :
                            p.iva_codigo === '4' ? 'IVA 15%' :
                            p.iva_codigo === '5' ? 'IVA 5%' :
                            p.iva_codigo === '6' ? 'No Objeto' :
                            p.iva_codigo === '7' ? 'Exento' :
                            p.iva_codigo === '8' ? 'Diferenciado' :
                            p.iva_codigo === '3' ? 'IVA 14%' :
                            p.iva_codigo === '10' ? 'IVA 13%' :
                            `Código ${p.iva_codigo}`}
                        </span>
                    </td>
                    <td className="px-4 py-2">
                    {p.imagen ? ( <img src={ p.imagen.startsWith("http") ? p.imagen : `${process.env.REACT_APP_API_URL}${p.imagen}` } alt={p.nombre} className="w-16 h-16 object-cover rounded" /> ) : ( "—" )}
                    </td>
                    <td className="px-4 py-2 text-center flex justify-center gap-2">
                    <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(p)} > Editar </button>
                    <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(p.id_producto)} > Eliminar </button>
                    </td>
                </tr>
                ))
            ) : (
                <tr>
                <td colSpan={usuarioLogueado?.isSuperAdmin ? "9" : "8"} className="text-center py-4 text-gray-500" > No hay productos registrados </td>
                </tr>
            )}
            </tbody>
            </table>
        </div>

        {/* Paginación */}
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

        {/* Modal Crear / Editar */}
        {modalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg w-[70%] max-h-[90vh] overflow-y-auto px-10 py-6 relative">
                <button onClick={() => setModalOpen(false)} className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl"> ✖ </button>
                <h2 className="text-2xl font-semibold mb-6 text-center sticky top-0 bg-white pb-2"> {editMode ? "Editar Producto" : "Nuevo Producto"} </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    <InputGroup label="Categoría" error={errors.id_categoria}>
                        <div className="flex gap-2">
                            <select name="id_categoria" value={producto.id_categoria} onChange={handleChange} className="input-base w-full" >
                            <option value="">Seleccione una categoría</option>
                            {categorias.map((c) => (
                                <option key={c.id_categoria} value={c.id_categoria}> {c.nombre} </option>
                            ))}
                            </select>
                            {/* Botón para crear nueva categoría */}
                            <button 
                                type="button" 
                                onClick={() => {
                                    setNuevaCategoria({ 
                                        nombre: "", 
                                        descripcion: "", 
                                        estado: "activo", 
                                        id_restaurante: usuarioLogueado?.isSuperAdmin ? "" : usuarioLogueado?.id_restaurante || ""
                                    });
                                    setCategoriaModal(true);
                                }} 
                                className="bg-green-500 hover:bg-green-600 text-white px-3 rounded" 
                            > + </button>
                        </div>
                    </InputGroup>

                    <InputGroup label="Nombre" error={errors.nombre}> <input name="nombre" value={producto.nombre} onChange={handleChange} placeholder="Ingrese nombre del producto" className="input-base" ref={(el) => (inputRefs.current["nombre"] = el)} /> </InputGroup>
                    <InputGroup label="Precio" error={errors.precio}> <input name="precio" value={producto.precio} onChange={handleChange} placeholder="Ej: 5.00" className="input-base" ref={(el) => (inputRefs.current["precio"] = el)} /> </InputGroup>
                    <InputGroup label="Descripción" error={errors.descripcion}> <textarea name="descripcion" value={producto.descripcion} onChange={handleChange} placeholder="Descripción del producto" className="input-base" ref={(el) => (inputRefs.current["descripcion"] = el)} /> </InputGroup>
                    <InputGroup label="Imagen">
                        <input type="file" name="imagen" accept="image/*" onChange={handleChange} className="input-base" />
                        {(previewImage || producto.imagen) && (
                            <img
                            src={previewImage || `${process.env.REACT_APP_API_URL}${producto.imagen}`}
                            alt="Previsualización"
                            className="w-32 h-32 mt-3 object-cover rounded border"
                            />
                        )}
                    </InputGroup>
                    <InputGroup label="Estado"> <select name="estado" value={producto.estado} onChange={handleChange} className="input-base"> <option value="activo">Activo</option> <option value="inactivo">Inactivo</option> </select> </InputGroup>
                    <InputGroup label="Código IVA">
                    <select name="iva_codigo" value={producto.iva_codigo} onChange={handleChange} className="input-base">
                        <option value="0">0 - IVA 0%</option>
                        <option value="2">2 - IVA 12%</option>
                        <option value="3">3 - IVA 14%</option>
                        <option value="4">4 - IVA 15%</option>
                        <option value="5">5 - IVA 5%</option>
                        <option value="6">6 - No Objeto de Impuesto</option>
                        <option value="7">7 - Exento de IVA</option>
                        <option value="8">8 - IVA Diferenciado</option>
                        <option value="10">10 - IVA 13%</option>
                    </select>
                    </InputGroup>
                    {/* Campo Restaurante - Solo visible para SuperAdmin */}
                    {usuarioLogueado?.isSuperAdmin && (
                        <InputGroup label="Restaurante" error={errors.id_restaurante}> <select name="id_restaurante" value={producto.id_restaurante} onChange={handleChange} className="input-base"> <option value="">Seleccione restaurante</option> {restaurantes.map((r) => ( <option key={r.id_restaurante} value={r.id_restaurante}> {r.nombre_comercial} </option> ))} </select> </InputGroup>
                    )}
                </div>
                <div className="flex justify-end gap-4 mt-6">
                    <button type="button" onClick={() => setModalOpen(false)} className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded"> Cancelar </button>
                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"> {editMode ? "Actualizar" : "Guardar"} </button>
                </div>
                </form>
            </div>
            </div>
        )}

        {/* Modal Crear Categoría */}
        {categoriaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-xl shadow-lg w-[30rem] max-h-[90vh] overflow-y-auto px-8 py-6 relative">
            <button onClick={() => setCategoriaModal(false)} className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl" > ✖ </button>
            <h2 className="text-2xl font-semibold mb-6 text-center sticky top-0 bg-white pb-2"> Nueva Categoría </h2>

            <form onSubmit={handleCreateCategoria} className="space-y-4">
                <InputGroup label="Nombre">
                <input name="nombre" value={nuevaCategoria.nombre} onChange={handleChangeCategoria} placeholder="Ej: Bebidas, Postres..." className="input-base" /> </InputGroup>
                <InputGroup label="Descripción"> <textarea name="descripcion" value={nuevaCategoria.descripcion} onChange={handleChangeCategoria} placeholder="Descripción opcional" className="input-base" /> </InputGroup>
                <InputGroup label="Estado"> <select name="estado" value={nuevaCategoria.estado} onChange={handleChangeCategoria} className="input-base" > <option value="activo">Activo</option> <option value="inactivo">Inactivo</option> </select> </InputGroup>
                {/* Campo Restaurante - Solo visible para SuperAdmin */}
                {usuarioLogueado?.isSuperAdmin && (
                    <InputGroup label="Restaurante"> <select name="id_restaurante" value={nuevaCategoria.id_restaurante || ""} onChange={handleChangeCategoria} className="input-base" > <option value="">Seleccione restaurante</option> {restaurantes.map((r) => ( <option key={r.id_restaurante} value={r.id_restaurante}> {r.nombre_comercial} </option> ))} </select> </InputGroup>
                )}
                <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setCategoriaModal(false)} className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-5 py-2 rounded" > Cancelar </button>
                <button type="submit" className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded" > Guardar </button>
                </div>
            </form>
            </div>
        </div>
        )}
        </div>
    );
};

export default ProductosPage;
